'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import type { BoardEntry } from '@/lib/boards';

/**
 * The "Make an enquiry" button that sits on every board entry.
 *
 * Opens a small dialog rather than a page, so nobody loses their place on the
 * board. If the visitor is signed in, their name and email are filled in for
 * them — most enquiries come from members, and retyping it is friction for no
 * reason.
 */
export default function BoardEnquiryButton({
  entry,
  label = 'Make an enquiry',
  className = 'btn-primary text-sm py-2',
}: {
  entry: BoardEntry;
  label?: string;
  className?: string;
}) {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  // Prefill from the signed-in member once their profile has loaded.
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({
      ...f,
      name: f.name || profile?.full_name || profile?.business_name || '',
      email: f.email || profile?.email || user?.email || '',
      phone: f.phone || profile?.phone || '',
    }));
  }, [open, profile, user]);

  const close = () => {
    setOpen(false);
    setError(null);
    setSent(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/board-enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryId: entry.id,
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not send that enquiry.');
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that enquiry.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <Icon name="EnvelopeIcon" size={16} />
        {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Enquire about ${entry.title}`}
          onClick={close}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-1">
              <h3 className="text-lg font-bold text-foreground">Make an enquiry</h3>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground p-1"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              About <span className="font-semibold text-foreground">{entry.title}</span>
            </p>

            {sent ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={28} className="text-green-600" />
                </div>
                <p className="font-bold text-foreground mb-1">Enquiry sent</p>
                <p className="text-sm text-muted-foreground">
                  The FarmTradeDirect team will be in touch. We&apos;ve got your details, so
                  there&apos;s nothing else you need to do.
                </p>
                <button type="button" className="btn-primary text-sm mt-5" onClick={close}>
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-4">
                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3" role="alert">
                    <Icon name="ExclamationTriangleIcon" size={18} className="text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label" htmlFor="be-name">Your name *</label>
                    <input
                      id="be-name"
                      className="form-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="be-email">Email *</label>
                    <input
                      id="be-email"
                      type="email"
                      className="form-input"
                      required
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="form-label" htmlFor="be-phone">Phone</label>
                  <input
                    id="be-phone"
                    type="tel"
                    className="form-input"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                  <p className="form-helper">Usually the quickest way to get an answer.</p>
                </div>

                <div>
                  <label className="form-label" htmlFor="be-message">Your enquiry</label>
                  <textarea
                    id="be-message"
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Dates, quantities, where you are — whatever's relevant."
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  />
                </div>

                <div className="flex gap-3">
                  <button type="submit" className="btn-primary text-sm flex-1 disabled:opacity-60" disabled={sending}>
                    <Icon
                      name={sending ? 'ArrowPathIcon' : 'PaperAirplaneIcon'}
                      size={16}
                      className={sending ? 'animate-spin' : undefined}
                    />
                    {sending ? 'Sending…' : 'Send enquiry'}
                  </button>
                  <button type="button" className="btn-secondary text-sm" onClick={close}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
